import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';

import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosClient';
import { useAuth } from '../../context/AuthContext';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.ve-root {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  font-family: 'Outfit', sans-serif;
  padding: 24px;
}

.ve-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 18px;
  padding: 40px 36px;
  box-shadow: 0 20px 50px rgba(0,0,0,.06);
  text-align: center;
}

.ve-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255,215,0,.12);
  border: 1.5px solid rgba(255,215,0,.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 1.3rem;
  color: #b8960c;
}

.ve-title {
  font-size: 1.5rem;
  font-weight: 900;
  color: #0d0d0d;
  margin-bottom: 8px;
  letter-spacing: -.02em;
}

.ve-sub {
  font-size: .85rem;
  color: #64748b;
  margin-bottom: 28px;
  line-height: 1.6;
  font-weight: 500;
}

.ve-sub strong {
  color: #0d0d0d;
  overflow-wrap: anywhere;
}

.ve-err,
.ve-info {
  border-radius: 10px;
  padding: 11px 14px;
  margin-bottom: 18px;
  font-size: .8rem;
  font-weight: 600;
  text-align: left;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.ve-err {
  background: #fef2f2;
  border: 1.5px solid #fecaca;
  color: #b91c1c;
}

.ve-info {
  background: #f0fdf4;
  border: 1.5px solid #bbf7d0;
  color: #15803d;
}

.ve-code-row {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 22px;
}

.ve-code-input {
  width: 44px;
  height: 54px;
  text-align: center;
  font-size: 1.3rem;
  font-weight: 700;
  color: #0d0d0d;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
  font-family: 'Outfit', sans-serif;
  transition: border-color .15s, box-shadow .15s;
  background: #fff;
}

.ve-code-input:focus {
  border-color: #FFD700;
  box-shadow: 0 0 0 4px rgba(255,215,0,.12);
}

.ve-code-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.ve-btn {
  width: 100%;
  padding: 14px;
  font-size: .92rem;
  font-weight: 800;
  background: linear-gradient(135deg, #FFD700, #e6c200);
  color: #0d0d0d;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-family: 'Outfit', sans-serif;
  transition: all .2s;
  box-shadow: 0 6px 22px rgba(255,215,0,.35);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
}

.ve-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 36px rgba(255,215,0,.45);
}

.ve-btn:disabled {
  opacity: .5;
  cursor: not-allowed;
  transform: none;
}

@keyframes ve-spin {
  to { transform: rotate(360deg); }
}

.ve-spin {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2.5px solid rgba(0,0,0,.2);
  border-top-color: #0d0d0d;
  border-radius: 50%;
  animation: ve-spin .7s linear infinite;
}

.ve-resend {
  font-size: .8rem;
  color: #64748b;
  font-weight: 500;
}

.ve-resend button {
  background: none;
  border: none;
  color: #0d0d0d;
  font-weight: 800;
  cursor: pointer;
  font-family: 'Outfit', sans-serif;
  font-size: .8rem;
  border-bottom: 2px solid #FFD700;
  padding: 0 0 1px;
}

.ve-resend button:disabled {
  color: #94a3b8;
  border-color: transparent;
  cursor: not-allowed;
}

@media (max-width: 420px) {
  .ve-card {
    padding: 32px 20px;
  }

  .ve-code-row {
    gap: 6px;
  }

  .ve-code-input {
    width: 40px;
    height: 50px;
  }
}
`;

const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 60;

/*
 * Resolve the user's dashboard.
 * The backend dashboard value takes priority.
 */
const getDashboardPath = (user) => {
  if (user?.dashboard) {
    return user.dashboard;
  }

  const role = String(
    user?.active_role || user?.role || ''
  ).toLowerCase().trim();

  switch (role) {
    case 'client':
      return '/hire';

    case 'craftsman':
      return '/craftsman/dashboard';

    case 'admin':
      return '/admin/dashboard';

    default:
      return null;
  }
};

/*
 * Extract readable backend error messages.
 */
const getErrorMessage = (err, fallback) => {
  const data = err?.response?.data;

  if (typeof data?.detail === 'string') {
    return data.detail;
  }

  if (typeof data?.message === 'string') {
    return data.message;
  }

  if (typeof data === 'string') {
    return data;
  }

  return fallback;
};

const VerifyEmail = () => {
  const navigate = useNavigate();

  // Connect to the global authentication context.
  const { updateUser } = useAuth();

  const [digits, setDigits] = useState(
    Array(CODE_LENGTH).fill('')
  );

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cooldown, setCooldown] = useState(0);

  const inputsRef = useRef([]);
  const mountedRef = useRef(false);
  const sendingRef = useRef(false);
  const verifyingRef = useRef(false);

  /*
   * Keep global authentication state and local user details updated.
   */
  const syncUser = useCallback(
    (user) => {
      if (!user) return;

      updateUser(user);
      setEmail(user.email || '');
    },
    [updateUser]
  );

  /*
   * Redirect to the correct dashboard.
   */
  const redirectToDashboard = useCallback(
    (user) => {
      const dashboard = getDashboardPath(user);

      if (!dashboard) {
        setError(
          'Your account role could not be identified. Please contact support.'
        );

        return false;
      }

      navigate(dashboard, { replace: true });

      return true;
    },
    [navigate]
  );

  /*
   * Send or resend the verification OTP.
   */
  const sendOtp = useCallback(
    async (isResend = false) => {
      if (sendingRef.current || verifyingRef.current) {
        return;
      }

      sendingRef.current = true;

      if (mountedRef.current) {
        setSending(true);
        setError('');
        setInfo('');
      }

      try {
        await api.post('/email/send-otp/', {});

        if (!mountedRef.current) return;

        setInfo(
          isResend
            ? 'A new verification code has been sent to your email.'
            : 'A verification code has been sent to your email.'
        );

        setDigits(Array(CODE_LENGTH).fill(''));
        setCooldown(RESEND_COOLDOWN);

      } catch (err) {
        if (!mountedRef.current) return;

        const status = err.response?.status;

        if (status === 429) {
          setInfo(
            'A verification code was sent recently. Please check your inbox or wait before requesting another.'
          );

          setCooldown(RESEND_COOLDOWN);

        } else if (status === 400) {
          /*
           * Confirm whether the account is already verified.
           */
          try {
            const response = await api.get('/me/');

            if (!mountedRef.current) return;

            const user = response.data;

            syncUser(user);

            if (user.is_verified === true) {
              redirectToDashboard(user);
            } else {
              setError(
                getErrorMessage(
                  err,
                  'The verification code could not be sent. Please try again.'
                )
              );
            }

          } catch (checkError) {
            if (mountedRef.current) {
              setError(
                'We could not confirm your verification status. Please refresh and try again.'
              );
            }
          }

        } else if (status === 401) {
          navigate('/login', { replace: true });

        } else {
          setError(
            getErrorMessage(
              err,
              'Could not send the verification code. Please try again.'
            )
          );
        }

      } finally {
        sendingRef.current = false;

        if (mountedRef.current) {
          setSending(false);
        }
      }
    },
    [navigate, redirectToDashboard, syncUser]
  );

  /*
   * Check the currently authenticated account.
   */
  useEffect(() => {
    let active = true;

    mountedRef.current = true;

    const initializeVerification = async () => {
      try {
        const response = await api.get('/me/');

        if (!active) return;

        const user = response.data;

        syncUser(user);

        if (user.is_verified === true) {
          redirectToDashboard(user);
          return;
        }

        await sendOtp(false);

      } catch (err) {
        if (!active) return;

        if (err.response?.status === 401) {
          navigate('/login', { replace: true });
        } else {
          setError(
            getErrorMessage(
              err,
              'We could not load your account. Please refresh and try again.'
            )
          );
        }

      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    initializeVerification();

    return () => {
      active = false;
      mountedRef.current = false;
    };
  }, [navigate, redirectToDashboard, sendOtp, syncUser]);

  /*
   * Resend countdown.
   */
  useEffect(() => {
    if (cooldown <= 0) return undefined;

    const timer = setTimeout(() => {
      setCooldown((current) => Math.max(0, current - 1));
    }, 1000);

    return () => clearTimeout(timer);
  }, [cooldown]);

  /*
   * Handle individual OTP input.
   */
  const handleDigitChange = (index, value) => {
    const numericValue = value.replace(/\D/g, '');

    const next = [...digits];

    if (!numericValue) {
      next[index] = '';
      setDigits(next);
      return;
    }

    next[index] = numericValue.slice(-1);

    setDigits(next);

    if (index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  /*
   * Keyboard navigation.
   */
  const handleKeyDown = (index, event) => {
    if (
      event.key === 'Backspace' &&
      !digits[index] &&
      index > 0
    ) {
      inputsRef.current[index - 1]?.focus();
    }

    if (
      event.key === 'ArrowLeft' &&
      index > 0
    ) {
      inputsRef.current[index - 1]?.focus();
    }

    if (
      event.key === 'ArrowRight' &&
      index < CODE_LENGTH - 1
    ) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  /*
   * Paste a complete six-digit OTP.
   */
  const handlePaste = (event) => {
    const pasted = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH);

    if (!pasted) return;

    event.preventDefault();

    const next = Array.from(
      { length: CODE_LENGTH },
      (_, index) => pasted[index] || ''
    );

    setDigits(next);

    const focusIndex = Math.min(
      pasted.length,
      CODE_LENGTH - 1
    );

    inputsRef.current[focusIndex]?.focus();
  };

  /*
   * Verify the entered OTP.
   */
  const handleVerify = async (event) => {
    event.preventDefault();

    if (verifyingRef.current || sendingRef.current) {
      return;
    }

    const code = digits.join('');

    if (code.length !== CODE_LENGTH) {
      setError(
        `Please enter the complete ${CODE_LENGTH}-digit code.`
      );

      return;
    }

    verifyingRef.current = true;

    setError('');
    setInfo('');
    setVerifying(true);

    try {
      const response = await api.post(
        '/email/verify-otp/',
        { code }
      );

      if (!mountedRef.current) return;

      /*
       * The backend returns:
       * {
       *   detail: "...",
       *   user: {
       *     id: ...,
       *     email: "...",
       *     role: "...",
       *     active_role: "...",
       *     is_verified: true,
       *     dashboard: "..."
       *   }
       * }
       */
      const verifiedUser = response.data?.user;

      if (!verifiedUser) {
        throw new Error(
          'Verification succeeded, but account information was not returned.'
        );
      }

      if (verifiedUser.is_verified !== true) {
        throw new Error(
          'Your email verification could not be confirmed. Please try again.'
        );
      }

      /*
       * Update the global authentication state.
       */
      syncUser(verifiedUser);

      /*
       * Resolve the correct dashboard.
       */
      const dashboard = getDashboardPath(verifiedUser);

      if (!dashboard) {
        throw new Error(
          'Your account role could not be identified. Please contact support.'
        );
      }

      /*
       * Redirect the verified user.
       */
      navigate(dashboard, { replace: true });

    } catch (err) {
      if (!mountedRef.current) return;

      if (err.response?.status === 401) {
        setError(
          'Your session has expired. Please log in again before verifying your email.'
        );
      } else {
        setError(
          getErrorMessage(
            err,
            err.message ||
              'Invalid or expired verification code. Please try again.'
          )
        );
      }

    } finally {
      verifyingRef.current = false;

      if (mountedRef.current) {
        setVerifying(false);
      }
    }
  };

  return (
    <>
      <style>{CSS}</style>

      <div className="ve-root">
        <div className="ve-card">

          <div className="ve-icon">
            <i className="fas fa-envelope-circle-check" />
          </div>

          <h2 className="ve-title">
            Verify your email
          </h2>

          <p className="ve-sub">
            {loading ? (
              'Checking your account...'
            ) : (
              <>
                Enter the {CODE_LENGTH}-digit code sent to{' '}
                <strong>{email || 'your email'}</strong>
              </>
            )}
          </p>

          {error && (
            <div className="ve-err" role="alert">
              <i className="fas fa-exclamation-circle" />
              <span>{error}</span>
            </div>
          )}

          {info && !error && (
            <div className="ve-info" role="status">
              <i className="fas fa-circle-check" />
              <span>{info}</span>
            </div>
          )}

          {loading ? (
            <button
              type="button"
              className="ve-btn"
              disabled
            >
              <span className="ve-spin" />
              Loading...
            </button>
          ) : (
            <>
              <form onSubmit={handleVerify}>
                <div
                  className="ve-code-row"
                  onPaste={handlePaste}
                >
                  {digits.map((digit, index) => (
                    <input
                      key={index}
                      ref={(element) => {
                        inputsRef.current[index] = element;
                      }}
                      className="ve-code-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete={
                        index === 0
                          ? 'one-time-code'
                          : 'off'
                      }
                      pattern="[0-9]*"
                      maxLength={1}
                      aria-label={`Verification code digit ${index + 1}`}
                      value={digit}
                      onChange={(event) =>
                        handleDigitChange(
                          index,
                          event.target.value
                        )
                      }
                      onKeyDown={(event) =>
                        handleKeyDown(index, event)
                      }
                      disabled={verifying || sending}
                    />
                  ))}
                </div>

                <button
                  type="submit"
                  className="ve-btn"
                  disabled={
                    verifying ||
                    sending ||
                    digits.join('').length !== CODE_LENGTH
                  }
                >
                  {verifying ? (
                    <>
                      <span className="ve-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check" />
                      Verify email
                    </>
                  )}
                </button>
              </form>

              <p className="ve-resend">
                Didn't get a code?{' '}
                <button
                  type="button"
                  onClick={() => sendOtp(true)}
                  disabled={
                    sending ||
                    verifying ||
                    cooldown > 0
                  }
                >
                  {sending
                    ? 'Sending...'
                    : cooldown > 0
                      ? `Resend in ${cooldown}s`
                      : 'Resend code'}
                </button>
              </p>
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default VerifyEmail;
